import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  deleteDoc,
  orderBy,
  increment
} from 'firebase/firestore';
import { db } from './firebase';

// Collection pour les projets
const PROJECTS_COLLECTION = 'projects';

// Fonction pour générer un slug à partir d'un nom
const generateSlug = (name) => {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Enlever les accents
    .replace(/[^a-z0-9]+/g, '-') // Remplacer les caractères spéciaux par des tirets
    .replace(/^-+|-+$/g, '') // Enlever les tirets au début et à la fin
    .replace(/-+/g, '-'); // Remplacer les multiples tirets par un seul
};

// Créer un nouveau projet
export const createProject = async (userId, projectName, code) => {
  if (import.meta.env.MODE !== 'production') {
    console.log('[FIREBASE] Requête: createProject', { userId, projectName });
  }
  try {
    const projectsCollection = collection(db, PROJECTS_COLLECTION);
    const slug = generateSlug(projectName);
    const newProject = {
      userId,
      name: projectName,
      slug,
      code,
      isPublic: false, // Par défaut, les projets sont privés
      viewCount: 0,    // Compteur de vues initial
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const docRef = await addDoc(projectsCollection, newProject);
    return {
      id: docRef.id,
      ...newProject
    };
  } catch (error) {
    console.error("Erreur lors de la création du projet:", error);
    throw error;
  }
};

// Mettre à jour un projet existant
export const updateProject = async (projectId, updates) => {
  if (import.meta.env.MODE !== 'production') {
    console.log('[FIREBASE] Requête: updateProject', { projectId, updates });
  }
  try {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    const updateData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };

    // Si le nom est mis à jour, générer un nouveau slug
    if (updates.name) {
      updateData.slug = generateSlug(updates.name);
    }
    
    await updateDoc(projectRef, updateData);
    return { id: projectId, ...updateData };
  } catch (error) {
    console.error("Erreur lors de la mise à jour du projet:", error);
    throw error;
  }
};

// Obtenir tous les projets d'un utilisateur (sans le code)
export const getUserProjects = async (userId) => {
  if (import.meta.env.MODE !== 'production') {
    console.log('[FIREBASE] Requête: getUserProjects', { userId });
  }
  try {
    const projectsCollection = collection(db, PROJECTS_COLLECTION);
    const q = query(
      projectsCollection,
      where("userId", "==", userId),
      orderBy("updatedAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    const projects = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Exclure le code du projet des données retournées
      const { code, ...projectMetadata } = data;
      projects.push({
        id: doc.id,
        ...projectMetadata
      });
    });
    
    return projects;
  } catch (error) {
    console.error("Erreur lors de la récupération des projets:", error);
    throw error;
  }
};

// Obtenir un projet spécifique par son ID (avec le code)
export const getProject = async (projectId, userId) => {
  if (import.meta.env.MODE !== 'production') {
    console.log('[FIREBASE] Requête: getProject', { projectId, userId });
  }
  try {
    const projectsCollection = collection(db, PROJECTS_COLLECTION);
    const q = query(
      projectsCollection,
      where("userId", "==", userId),
      where("slug", "==", projectId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('Projet non trouvé ou non autorisé');
    }
    
    const docSnap = querySnapshot.docs[0];
    return {
      id: docSnap.id,
      ...docSnap.data()
    };
  } catch (error) {
    console.error('Erreur lors de la récupération du projet:', error);
    throw error;
  }
};

// Supprimer un projet
export const deleteProject = async (projectId) => {
  if (import.meta.env.MODE !== 'production') {
    console.log('[FIREBASE] Requête: deleteProject', { projectId });
  }
  try {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    await deleteDoc(projectRef);
    return true;
  } catch (error) {
    console.error("Erreur lors de la suppression du projet:", error);
    throw error;
  }
};

// Obtenir les projets publics d'un utilisateur par son alias
export const getPublicProjectsByAlias = async (alias) => {
  if (import.meta.env.MODE !== 'production') {
    console.log('[FIREBASE] Requête: getPublicProjectsByAlias', { alias });
  }
  try {
    // D'abord, obtenir l'ID de l'utilisateur à partir de son alias
    const usersCollection = collection(db, 'users');
    const userQuery = query(usersCollection, where("alias", "==", alias));
    const userSnapshot = await getDocs(userQuery);
    
    if (userSnapshot.empty) {
      throw new Error('Utilisateur non trouvé');
    }
    
    const userId = userSnapshot.docs[0].id;
    
    // Ensuite, obtenir les projets publics de cet utilisateur
    const projectsCollection = collection(db, PROJECTS_COLLECTION);
    const projectsQuery = query(
      projectsCollection,
      where("userId", "==", userId),
      where("isPublic", "==", true),
      orderBy("updatedAt", "desc")
    );
    
    const projectsSnapshot = await getDocs(projectsQuery);
    const projects = [];
    
    projectsSnapshot.forEach((doc) => {
      const data = doc.data();
      projects.push({
        id: doc.id,
        ...data
      });
    });
    
    return projects;
  } catch (error) {
    console.error("Erreur lors de la récupération des projets publics:", error);
    throw error;
  }
};

// Obtenir un projet public spécifique
export const getPublicProject = async (projectSlug, userAlias) => {
  if (import.meta.env.MODE !== 'production') {
    console.log('[FIREBASE] Requête: getPublicProject', { projectSlug, userAlias });
  }
  try {
    // D'abord, obtenir l'ID de l'utilisateur à partir de son alias
    const usersCollection = collection(db, 'users');
    const userQuery = query(usersCollection, where("alias", "==", userAlias));
    const userSnapshot = await getDocs(userQuery);
    
    if (userSnapshot.empty) {
      throw new Error('Utilisateur non trouvé');
    }
    
    const userId = userSnapshot.docs[0].id;
    
    // Ensuite, obtenir le projet public spécifique
    const projectsCollection = collection(db, PROJECTS_COLLECTION);
    const projectQuery = query(
      projectsCollection,
      where("userId", "==", userId),
      where("slug", "==", projectSlug),
      where("isPublic", "==", true)
    );
    
    const projectSnapshot = await getDocs(projectQuery);
    
    if (projectSnapshot.empty) {
      throw new Error('Projet non trouvé ou non public');
    }
    
    const docSnap = projectSnapshot.docs[0];
    return {
      id: docSnap.id,
      ...docSnap.data()
    };
  } catch (error) {
    console.error('Erreur lors de la récupération du projet public:', error);
    throw error;
  }
};

// Incrémenter le compteur de vues d'un projet
export const incrementViewCount = async (projectId) => {
  if (import.meta.env.MODE !== 'production') {
    console.log('[FIREBASE] Requête: incrementViewCount', { projectId });
  }
  try {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    
    // Utiliser la fonction increment de Firestore pour éviter les problèmes de concurrence
    await updateDoc(projectRef, {
      viewCount: increment(1)
    });
    
    return true;
  } catch (error) {
    console.error("Erreur lors de l'incrémentation du compteur de vues:", error);
    // On ne propage pas l'erreur pour éviter de bloquer l'affichage du projet
    return false;
  }
}; 